import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactComponent as ArrowIcon } from '../assets/images/icons/ic-arrow.svg'
import api from '../api'
import Badge from '../components/Badge'
import Button from '../components/Button'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import JsonView from 'react18-json-view'
import 'react18-json-view/src/dark.css'
import 'react18-json-view/src/style.css'
import { formatDateV2, formatTimeV2 } from '../utils'
import typo from '../typography.module.css'
import styles from './DeveloperKeyDetails.module.css'

const formatDayLabel = (dateInput) => {
  const formattedDate = formatDateV2(dateInput, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  if (!formattedDate) {
    return 'N/A'
  }

  const todayLabel = formatDateV2(new Date(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  return formattedDate === todayLabel ? `${formattedDate} (Oggi)` : formattedDate
}

const formatTimeLabel = (dateInput) => {
  return formatTimeV2(dateInput, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }) || '--:--:--'
}

const getMethodColor = (method) => {
  switch ((method || '').toUpperCase()) {
    case 'GET':
      return 'var(--secondary)'
    case 'POST':
      return 'var(--sf-green)'
    case 'PUT':
    case 'PATCH':
      return 'var(--sf-orange)'
    case 'DELETE':
      return 'var(--sf-red)'
    default:
      return 'var(--text-color)'
  }
}

const getStatusColor = (status) => {
  if (status >= 200 && status < 300) {
    return 'var(--sf-green)'
  }
  if (status >= 400 && status < 500) {
    return 'var(--sf-orange)'
  }
  if (status >= 500) {
    return 'var(--sf-red)'
  }
  return 'var(--text-color)'
}

const DeveloperKeyDetails = () => {
  const navigate = useNavigate()
  const { publicKey } = useParams()

  const [loading, setLoading] = useState(false)
  const [keyInfo, setKeyInfo] = useState(null)
  const [calls, setCalls] = useState([])
  const [selectedCallId, setSelectedCallId] = useState(null)

  const loadCalls = useCallback(async () => {
    if (!publicKey) {
      return
    }

    setLoading(true)
    try {
      const response = await api.get(`/company/developers/keys/${encodeURIComponent(publicKey)}/calls?page=0&per=500`)
      setKeyInfo(response?.key || null)
      const fetchedCalls = response?.calls || []
      setCalls(fetchedCalls)
      setSelectedCallId((previousSelectedCallId) => {
        if (previousSelectedCallId && fetchedCalls.some((call) => call.id === previousSelectedCallId)) {
          return previousSelectedCallId
        }
        return fetchedCalls[0]?.id || null
      })
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }, [publicKey])

  useEffect(() => {
    loadCalls()
  }, [loadCalls])

  const selectedCall = useMemo(() => calls.find((call) => call.id === selectedCallId) || null, [calls, selectedCallId])

  const groupedCalls = useMemo(() => {
    const groups = []
    const groupsByDay = new Map()

    calls.forEach((call) => {
      const dayLabel = formatDayLabel(call.created_at)
      if (!groupsByDay.has(dayLabel)) {
        const group = { dayLabel, calls: [] }
        groupsByDay.set(dayLabel, group)
        groups.push(group)
      }

      groupsByDay.get(dayLabel).calls.push(call)
    })

    return groups
  }, [calls])

  const chartData = useMemo(() => {
    const byDay = new Map()

    calls.forEach((call) => {
      if (!call.created_at) {
        return
      }

      const createdAt = new Date(call.created_at)
      if (Number.isNaN(createdAt.getTime())) {
        return
      }

      const dayLabel = formatDayLabel(call.created_at)
      if (!byDay.has(dayLabel)) {
        byDay.set(dayLabel, {
          success: 0,
          failed: 0,
          sortTs: createdAt.getTime(),
        })
      }

      const dayEntry = byDay.get(dayLabel)
      if (Number(call.response_status) >= 400) {
        dayEntry.failed += 1
      } else {
        dayEntry.success += 1
      }
      dayEntry.sortTs = Math.min(dayEntry.sortTs, createdAt.getTime())
    })

    return Array.from(byDay.entries())
      .sort((a, b) => a[1].sortTs - b[1].sortTs)
      .map(([dayLabel, dayEntry]) => {
        return {
          dayKey: dayLabel,
          day: dayLabel,
          calls_success: dayEntry.success,
          calls_failed: dayEntry.failed,
          fullDate: dayLabel,
        }
      })
  }, [calls])

  const renderChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null
    }

    const completedValue = payload.find((item) => item.dataKey === 'calls_success')?.value ?? 0
    const failedValue = payload.find((item) => item.dataKey === 'calls_failed')?.value ?? 0

    return (
      <div className={styles.chartTooltipCard}>
        <div className={styles.chartTooltipTitle}>{label}</div>
        <div className={styles.chartTooltipRow}>
          <span className={styles.chartTooltipLabel}>Completate</span>
          <span className={styles.chartTooltipValueSuccess}>{completedValue}</span>
        </div>
        <div className={styles.chartTooltipRow}>
          <span className={styles.chartTooltipLabel}>Fallite</span>
          <span className={styles.chartTooltipValueFailed}>{failedValue}</span>
        </div>
      </div>
    )
  }

  return (
    <HeaderFooterLayout>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Button
              appearance='subtle'
              onClick={() => navigate('/developers')}
              style={{ alignSelf: 'flex-start' }}
            >
              <ArrowIcon style={{ width: '11px', height: '11px' }} />
              Torna alle chiavi
            </Button>

            {keyInfo && (
              <div className={styles.titleRow}>
                <div className={typo.title}>{keyInfo.public}</div>
                {keyInfo.active
                  ? <Badge size={'small'} name={'ATTIVA'} color={'var(--sf-green)'} />
                  : <Badge size={'small'} name={'REVOCATA'} color={'var(--sf-orange)'} />}
              </div>
            )}

            {!keyInfo && <div className={typo.title}>Dettaglio Chiave</div>}

            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div className={styles.chartTitle}>Chiamate</div>
                <div className={styles.chartMetric}>
                  <span className={styles.chartMetricLabel}>TOTALE</span>
                  <span className={styles.chartMetricValue}>{keyInfo?.total_calls ?? 0}</span>
                </div>
              </div>
              {chartData.length === 0 ? (
                <div className={styles.emptyState}>Nessun dato disponibile per il grafico.</div>
              ) : (
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width='100%' height={260}>
                    <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke='rgba(var(--text-color-rgb), 12%)' />
                      <XAxis dataKey='day' tick={{ fontSize: 12, fill: 'rgba(var(--text-color-rgb), 66%)' }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'rgba(var(--text-color-rgb), 66%)' }} />
                      <Tooltip
                        content={renderChartTooltip}
                      />
                      <Line type='monotone' dataKey='calls_success' name='Completate' stroke='var(--sf-green)' strokeWidth={2.2} dot={{ r: 3 }} />
                      <Line type='monotone' dataKey='calls_failed' name='Fallite' stroke='var(--sf-red)' strokeWidth={2.2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className={styles.detailsLayout}>
              <div className={styles.callsPanel}>
                {loading && <div className={styles.emptyState}>Caricamento chiamate...</div>}

                {!loading && groupedCalls.length === 0 && (
                  <div className={styles.emptyState}>Nessuna chiamata disponibile per questa chiave.</div>
                )}

                {!loading && groupedCalls.map((group) => (
                  <div key={group.dayLabel} className={styles.dayGroup}>
                    <div className={styles.dayTitle}>{group.dayLabel}</div>
                    <div className={styles.callsList}>
                      {group.calls.map((call) => {
                        const isActive = selectedCallId === call.id
                        const isFailed = Number(call.response_status) >= 400
                        return (
                          <button
                            key={call.id}
                            type='button'
                            className={`${styles.callItem} ${isFailed ? styles.callItemFailed : ''} ${isActive ? styles.callItemActive : ''} ${isActive && isFailed ? styles.callItemActiveFailed : ''}`}
                            onClick={() => setSelectedCallId(call.id)}
                          >
                            <div className={styles.callTopRow}>
                              <Badge size={'small'} name={(call.method || 'N/A').toUpperCase()} color={getMethodColor(call.method)} />
                              <div className={styles.callMeta}>
                                <span className={styles.callTime}>{formatTimeLabel(call.created_at)}</span>
                                <span className={styles.callStatusMini} style={{ color: getStatusColor(call.response_status) }}>
                                  HTTP {call.response_status ?? 'N/A'}
                                </span>
                              </div>
                            </div>
                            <span className={styles.callPath}>{call.path}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.detailPanel}>
                {!selectedCall && <div className={styles.emptyState}>Seleziona una chiamata per vedere il dettaglio.</div>}

                {selectedCall && (
                  <div className={styles.dialogContent}>
                    <div className={styles.detailTopRow}>
                      <div className={styles.detailLine}><b>{selectedCall.method} {selectedCall.path}</b></div>
                      <div className={styles.detailHttpStatus} style={{ color: getStatusColor(selectedCall.response_status) }}>
                        <span className={styles.detailHttpLabel}>HTTP</span>
                        <span className={styles.detailHttpCode}>{selectedCall.response_status ?? 'N/A'}</span>
                      </div>
                    </div>

                    <div className={styles.detailLine}><b>IP Client:</b> {selectedCall.client_ip || 'N/A'}</div>

                    <details className={styles.jsonSection}>
                      <summary className={styles.jsonSummary}>HEADERS DELLA RICHIESTA</summary>
                      <div className={styles.jsonBody}>
                        <JsonView src={selectedCall.request_headers ?? {}} dark={true} theme='atom' enableClipboard={false} collapsed={1} />
                      </div>
                    </details>

                    <details className={styles.jsonSection}>
                      <summary className={styles.jsonSummary}>BODY DELLA RICHIESTA</summary>
                      <div className={styles.jsonBody}>
                        <JsonView src={selectedCall.request_payload ?? {}} dark={true} theme='atom' enableClipboard={false} collapsed={1} />
                      </div>
                    </details>

                    <details className={styles.jsonSection} open>
                      <summary className={styles.jsonSummary}>BODY DELLA RISPOSTA</summary>
                      <div className={styles.jsonBody}>
                        <JsonView src={selectedCall.response_payload ?? {}} dark={true} theme='atom' enableClipboard={false} collapsed={1} />
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  )
}

export default DeveloperKeyDetails
